import { Controller } from "@hotwired/stimulus"
import { fetchJsonWithCsrf } from "../util/common"

// Connects to the data-controller="video-upload"
export default class extends Controller {
  static values = {
    fileUploadsUrl: String
  }

  static targets = [
    "nextButton",
    "cancelButton",
    "videoName",
    "progressContainer",
    "progressBar",
    "progressPercentage",
    "input",
    "placeholder",
    "preview",
    "overlay",
    "statusMessage",
    "errorMessage",
    "videoId"
  ]

  xhr = null
  activeDestroyUrl = null
  previewObjectUrl = null
  stallCheckInterval = null
  lastProgressAt = null
  uploadStalled = false
  maxFileSizeInMegabytes = 5120 // 5 GB
  stallTimeoutSeconds = 30
  stallCheckIntervalMs = 5000

  connect() {
    this.setNextButtonDisabledState(!this.hasUploadedVideo())
  }

  disconnect() {
    this.stopStallCheck()
    this.revokePreviewObjectUrl()
  }

  async change() {
    const file = this.selectedFile()
    if (!file)
      return

    if (this.fileSizeExceedsLimit(file)) {
      this.handleFileTooLarge()
      return
    }

    if (this.isUploading()) {
      this.cancelUpload()
    }

    await this.uploadFile(file)
  }

  selectedFile() {
    return this.inputTarget.files && this.inputTarget.files[0]
  }

  fileSizeExceedsLimit(file) {
    const fileSizeInMegabytes = file.size / (1024 * 1024)
    return fileSizeInMegabytes > this.maxFileSizeInMegabytes
  }

  handleFileTooLarge() {
    this.setErrorMessage(`File size exceeds the maximum allowed size of ${this.maxFileSizeInMegabytes / 1024} GB. Please select a smaller file.`)
    this.clearFileInput()
  }

  isUploading() {
    return !!this.xhr
  }

  remove(event) {
    event.stopPropagation()
    event.preventDefault()

    if (this.isUploading()) {
      this.cancelUpload()
    }

    this.revokePreviewObjectUrl()
    this.setPreview("")
    this.setNextButtonDisabledState(true)
    this.setPreviewVisibility(false)
    this.setVideoName("")
    this.setErrorMessage("")
    this.clearVideoId()
    this.clearFileInput()
    this.destroyUploadRecord()
  }

  cancelUpload() {
    if (!this.xhr)
      return

    this.stopStallCheck()
    this.xhr.abort()
    this.xhr = null
    this.setNextButtonDisabledState(true)
    this.setCancelButtonVisibility(false)
    this.setStatusMessage("")
    this.setErrorMessage("")
    this.showProgress(false)
    this.setVideoName("")
    this.clearVideoId()
    this.clearFileInput()
    this.destroyUploadRecord()
  }

  preventDefault(event) {
    event.preventDefault()
  }

  async uploadFile(file) {
    this.prepareUploadUi(file)

    try {
      const fileUpload = await this.createFileUpload(file)
      this.setDestroyUrl(fileUpload.destroy_url)
      await this.uploadFileToS3(file, fileUpload)

      this.setStatusMessage("Finalizing...")
      const completedFileUpload = await this.completeFileUpload(fileUpload)

      this.finalizeUploadSuccess(file, completedFileUpload)
    }
    catch (error) {
      if (error.name === "AbortError")
        return

      this.handleUploadFailure(error)
    }
    finally {
      this.xhr = null
    }
  }

  prepareUploadUi(file) {
    this.uploadStalled = false
    this.setErrorMessage("")
    this.setStatusMessage("Uploading...")
    this.setVideoName(file.name)
    this.setCancelButtonVisibility(true)
    this.setNextButtonDisabledState(true)
    this.clearVideoId()
    this.clearDestroyUrl()
    this.showProgress(true)
    this.setProgress(0)
  }

  finalizeUploadSuccess(file, completedFileUpload) {
    this.setVideoId(completedFileUpload.uploadable_id)
    this.setStatusMessage("")
    this.setCancelButtonVisibility(false)
    this.showProgress(false)
    this.setPreview(this.previewUrl(file))
    this.setPreviewVisibility(true)
    this.setNextButtonDisabledState(false)
  }

  handleUploadFailure(error) {
    this.setStatusMessage("")
    this.setCancelButtonVisibility(false)
    this.showProgress(false)
    this.setNextButtonDisabledState(true)
    this.revokePreviewObjectUrl()
    this.setPreview("")
    this.setPreviewVisibility(false)
    this.setVideoName("")
    this.setErrorMessage(this.uploadErrorMessage(error))
    this.clearVideoId()
    this.clearFileInput()
    this.destroyUploadRecord({ silent: true })
  }

  async createFileUpload(file) {
    return fetchJsonWithCsrf(
      this.fileUploadsUrlValue,
      "POST",
      {
        file_upload: {
          filename: file.name,
          content_type: file.type,
          byte_size: file.size
        }
      }
    )
  }

  uploadFileToS3(file, fileUpload) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      this.xhr = xhr

      xhr.open(fileUpload.upload_method, fileUpload.upload_url, true)
      this.applyUploadHeaders(xhr, fileUpload.upload_headers)

      xhr.upload.addEventListener("progress", (event) => {
        this.lastProgressAt = Date.now()
        this.updateProgress(event)
      })

      xhr.addEventListener("load", () => {
        this.stopStallCheck()

        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
          return
        }

        reject(new Error("Upload to S3 failed"))
      })

      xhr.addEventListener("error", () => {
        this.stopStallCheck()
        reject(new Error("Upload to S3 failed"))
      })

      xhr.addEventListener("abort", () => {
        this.stopStallCheck()

        if (this.uploadStalled) {
          this.uploadStalled = false
          reject(new Error("Upload stalled — no progress for 30 seconds. Please check your connection and try again."))
          return
        }

        reject(new DOMException("Upload aborted", "AbortError"))
      })

      this.startStallCheck()
      xhr.send(file)
    })
  }

  startStallCheck() {
    this.lastProgressAt = Date.now()
    this.uploadStalled = false

    this.stallCheckInterval = setInterval(() => {
      if (!this.isUploadStalled()) return

      this.uploadStalled = true
      this.stopStallCheck()
      this.xhr?.abort()
    }, this.stallCheckIntervalMs)
  }

  stopStallCheck() {
    if (!this.stallCheckInterval) return

    clearInterval(this.stallCheckInterval)
    this.stallCheckInterval = null
    this.lastProgressAt = null
  }

  isUploadStalled() {
    if (!this.lastProgressAt) return false

    const elapsedSeconds = (Date.now() - this.lastProgressAt) / 1000
    return elapsedSeconds > this.stallTimeoutSeconds
  }

  uploadErrorMessage(error) {
    if (this.isConnectionError(error)) return error.message

    return `Video upload failed: ${error.message}. Please, contact support.`
  }

  isConnectionError(error) {
    return error.message.includes("Upload stalled")
  }

  applyUploadHeaders(xhr, uploadHeaders) {
    Object.entries(uploadHeaders || {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })
  }

  async completeFileUpload(fileUpload) {
    if (!fileUpload.complete_url) {
      throw new Error("Missing complete URL")
    }

    const completedFileUpload = await fetchJsonWithCsrf(fileUpload.complete_url, "POST")

    if (!completedFileUpload.uploadable_id) {
      throw new Error("Missing uploadable id")
    }

    return completedFileUpload
  }

  hasUploadedVideo() {
    return this.hasVideoIdTarget && this.videoIdTarget.value.length > 0
  }

  setVideoId(videoId) {
    if (!this.hasVideoIdTarget)
      return

    this.videoIdTarget.value = videoId
  }

  clearVideoId() {
    if (!this.hasVideoIdTarget)
      return

    this.videoIdTarget.value = ""
  }

  setDestroyUrl(destroyUrl) {
    this.activeDestroyUrl = destroyUrl
  }

  clearDestroyUrl() {
    this.activeDestroyUrl = null
  }

  async destroyUploadRecord({ silent = false } = {}) {
    if (!this.activeDestroyUrl)
      return

    const destroyUrl = this.activeDestroyUrl
    this.clearDestroyUrl()

    try {
      await fetchJsonWithCsrf(destroyUrl, "DELETE")
    }
    catch {
      if (!silent) {
        this.setErrorMessage("Failed to cancel upload. Please try again.")
      }
    }
  }

  updateProgress(e) {
    if (e.lengthComputable) {
      const percentComplete = Math.round((e.loaded / e.total) * 100)
      this.setProgress(percentComplete)
    }
  }

  setProgress(percentage) {
    this.progressBarTarget.style.width = `${percentage}%`
    this.progressPercentageTarget.textContent = `${percentage}%`
  }

  clearFileInput() {
    const input = this.inputTarget
    input.files = null
    input.value = ""
  }

  setVideoName(name) {
    this.videoNameTarget.classList.toggle("hidden", !name)
    this.videoNameTarget.textContent = name
  }

  setCancelButtonVisibility(flag) {
    this.cancelButtonTarget.classList.toggle("hidden", !flag)
  }

  setNextButtonDisabledState(flag) {
    this.nextButtonTarget.disabled = flag
  }

  setPreview(url) {
    this.previewTarget.src = url
    this.previewTarget.load()
  }

  setPreviewVisibility(flag) {
    this.previewTarget.classList.toggle("hidden", !flag)
    this.placeholderTarget.classList.toggle("hidden", flag)
    this.overlayTarget.classList.toggle("hidden", !flag)
  }

  showProgress(flag) {
    this.progressContainerTarget.classList.toggle("hidden", !flag)
    this.progressBarTarget.style.width = "0%"
    this.progressPercentageTarget.textContent = "0%"
  }

  setStatusMessage(message) {
    if (message) {
      this.statusMessageTarget.textContent = message
      this.statusMessageTarget.classList.remove("hidden")
    } else {
      this.statusMessageTarget.classList.add("hidden")
    }
  }

  setErrorMessage(message) {
    if (message) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
    } else {
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  previewUrl(file) {
    this.revokePreviewObjectUrl()
    this.previewObjectUrl = URL.createObjectURL(file)

    return this.previewObjectUrl
  }

  revokePreviewObjectUrl() {
    if (!this.previewObjectUrl)
      return

    URL.revokeObjectURL(this.previewObjectUrl)
    this.previewObjectUrl = null
  }
}
